if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      abortController = undefined;
      onVariantChangeUnsubscriber = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];
      formatInfoAbortController = undefined;
      titleFitObserver = undefined;
      titleFitFrame = undefined;
      titleFitIdle = undefined;
      titleFitWidth = undefined;

      constructor() {
        super();
        this.requestSequence = 0;
        this.quantityRequestSequence = 0;
        this.handleOptionValueChangeHandler = this.handleOptionValueChange.bind(this);
        this.fetchQuantityRulesHandler = this.fetchQuantityRules.bind(this);
        this.blockPositionHandler = this.handleBlocksPosition.bind(this);
        this.formatInfoClickHandler = this.handleFormatInfoClick.bind(this);
        this.formatInfoKeydownHandler = this.handleFormatInfoKeydown.bind(this);
        this.initializeProductSwapUtility();
      }

      get variantSelectors() {
        return this.querySelector('ml-product-options');
      }

      get productId() {
        return this.getAttribute('data-product-id');
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get quantityInput() {
        return this.querySelector('quantity-input input');
      }

      get productMediaWrapper() {
        return (this._productMediaWrapper = this._productMediaWrapper || this.querySelector('.product__media-wrapper'));
      }

      connectedCallback() {
        this.onVariantChangeUnsubscriber?.();
        this.onVariantChangeUnsubscriber = FoxTheme.pubsub.subscribe(
          FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChangeHandler,
        );

        this.initQuantityHandlers();

        this.currentVariant = this.getSelectedVariant(this);
        if (this.currentVariant) {
          this.updateMedia(this.currentVariant);
          this.hydrateReadableTypeUrl();
        }

        this.handleBlocksPosition();
        document.addEventListener('matchMobile', this.blockPositionHandler);
        document.addEventListener('unmatchMobile', this.blockPositionHandler);

        this.formatInfoAbortController?.abort();
        this.formatInfoAbortController = new AbortController();
        this.addEventListener('click', this.formatInfoClickHandler, {
          signal: this.formatInfoAbortController.signal,
        });
        this.addEventListener('keydown', this.formatInfoKeydownHandler, {
          signal: this.formatInfoAbortController.signal,
        });

        this.deferProductTitleFit();
      }

      fitProductTitle() {
        const title = this.querySelector('[data-product-title-fit]');
        const text = title?.querySelector('[data-product-title-fit-text]');
        if (!(title instanceof HTMLElement) || !(text instanceof HTMLElement)) return;

        const fitsThreeLines = () => {
          const lineHeight = Number.parseFloat(getComputedStyle(text).lineHeight);
          if (!Number.isFinite(lineHeight) || lineHeight <= 0) return true;
          return text.getBoundingClientRect().height <= lineHeight * 3 + 0.5;
        };

        title.style.setProperty('--mlp-product-title-fit-scale', '1');
        title.removeAttribute('data-product-title-condensed');

        if (!fitsThreeLines()) {
          let overflowingScale = 1;
          const baseFontSize = Number.parseFloat(getComputedStyle(text).fontSize);
          const bodyFontSize = Number.parseFloat(getComputedStyle(document.body).fontSize) || 16;
          // Three lines are a presentation preference, not a reason to make
          // long titles unreadable. At the body-size floor, allow more lines.
          let fittingScale = Math.min(1, Math.max(16, bodyFontSize) / baseFontSize);
          if (!Number.isFinite(fittingScale) || fittingScale <= 0) fittingScale = 1;

          title.style.setProperty('--mlp-product-title-fit-scale', fittingScale.toFixed(5));

          if (fitsThreeLines()) {
            // Seven bisection steps resolve the scale to better than one
            // percent without forcing fourteen synchronous layout passes.
            for (let step = 0; step < 7; step += 1) {
              const candidate = (fittingScale + overflowingScale) / 2;
              title.style.setProperty('--mlp-product-title-fit-scale', candidate.toFixed(5));

              if (fitsThreeLines()) fittingScale = candidate;
              else overflowingScale = candidate;
            }
          }

          // Round upward so the final scale never falls below the readable floor.
          title.style.setProperty('--mlp-product-title-fit-scale', (Math.ceil(fittingScale * 100000) / 100000).toFixed(5));
          title.setAttribute('data-product-title-condensed', '');
        }

        title.setAttribute('data-product-title-fit-ready', '');

        const fittedFontSize = Number.parseFloat(getComputedStyle(text).fontSize);
        if (Number.isFinite(fittedFontSize) && fittedFontSize > 0) {
          this.style.setProperty('--mlp-product-title-fitted-font-size', fittedFontSize + 'px');
        }
      }

      queueProductTitleFit() {
        cancelAnimationFrame(this.titleFitFrame);
        this.titleFitFrame = requestAnimationFrame(() => this.fitProductTitle());
      }

      deferProductTitleFit() {
        const start = () => {
          this.titleFitIdle = undefined;
          if (this.isConnected) this.setupProductTitleFit();
        };

        if ('requestIdleCallback' in window) {
          this.titleFitIdle = requestIdleCallback(start, { timeout: 1200 });
        } else {
          this.titleFitIdle = setTimeout(start, 0);
        }
      }

      setupProductTitleFit() {
        this.titleFitObserver?.disconnect();
        cancelAnimationFrame(this.titleFitFrame);

        const title = this.querySelector('[data-product-title-fit]');
        if (!(title instanceof HTMLElement)) return;

        this.fitProductTitle();

        const widthOwner = title.closest('.product__block--title') || title.parentElement;
        if (widthOwner instanceof HTMLElement && typeof ResizeObserver !== 'undefined') {
          this.titleFitWidth = widthOwner.getBoundingClientRect().width;
          this.titleFitObserver = new ResizeObserver(([entry]) => {
            const nextWidth = entry?.contentRect?.width;
            if (!Number.isFinite(nextWidth) || Math.abs(nextWidth - this.titleFitWidth) < 0.5) return;
            this.titleFitWidth = nextWidth;
            this.queueProductTitleFit();
          });
          this.titleFitObserver.observe(widthOwner);
        }

        document.fonts?.ready.then(() => {
          if (this.isConnected) this.queueProductTitleFit();
        });
      }

      closeFormatCard(card, restoreFocus = false) {
        if (!(card instanceof HTMLElement)) return;

        const trigger = card.querySelector('[data-format-info-open]');
        const panel = card.querySelector('[data-format-info-panel]');

        card.classList.remove('is-format-info-open');
        trigger?.setAttribute('aria-expanded', 'false');
        if (panel instanceof HTMLElement) panel.hidden = true;

        if (restoreFocus && trigger instanceof HTMLElement && trigger.isConnected) {
          trigger.focus({ preventScroll: true });
        }
      }

      openFormatCard(trigger) {
        const card = trigger.closest('.variant-card');
        const panel = card?.querySelector('[data-format-info-panel]');
        if (!(card instanceof HTMLElement) || !(panel instanceof HTMLElement)) return;

        this.querySelectorAll('.variant-card.is-format-info-open').forEach((openCard) => {
          if (openCard !== card) this.closeFormatCard(openCard);
        });

        card.classList.add('is-format-info-open');
        trigger.setAttribute('aria-expanded', 'true');
        panel.hidden = false;
        panel.querySelector('[data-format-info-close]')?.focus({ preventScroll: true });
      }

      handleFormatInfoClick(event) {
        const target = event.target;
        if (!(target instanceof Element)) return;

        const trigger = target.closest('[data-format-info-open]');
        if (trigger instanceof HTMLButtonElement && this.contains(trigger)) {
          event.preventDefault();
          this.openFormatCard(trigger);
          return;
        }

        const close = target.closest('[data-format-info-close]');
        if (close && this.contains(close)) {
          event.preventDefault();
          this.closeFormatCard(close.closest('.variant-card'), true);
        }
      }

      handleFormatInfoKeydown(event) {
        if (event.key !== 'Escape') return;

        const card = this.querySelector('.variant-card.is-format-info-open');
        if (!card) return;

        event.preventDefault();
        this.closeFormatCard(card, true);
      }

      handleBlocksPosition() {
        const isMobile = FoxTheme.config.mqlMobile;
        const moveContent = (source, destination) => {
          const focused = document.activeElement;
          const restoreFocus = source.contains(focused);
          destination.replaceChildren(...source.childNodes);
          // Reparenting disconnects focused descendants even though we reuse them.
          // Restore only a lost focus, never another component's deliberate focus.
          if (restoreFocus && focused.isConnected && document.activeElement === document.body) {
            focused.focus({ preventScroll: true });
          }
        };
        // Move the one Liquid-rendered panel, including listeners, Lit parts and
        // review state. Empty layout slots never duplicate descriptions or data.
        this.querySelectorAll('[data-product-detail-slot][data-slot-layout="mobile"]').forEach(mobile => {
          const desktop = this.querySelector(`[data-product-detail-slot="${CSS.escape(mobile.dataset.productDetailSlot)}"][data-slot-layout="desktop"]`);
          if (!desktop) return;
          const source = isMobile ? desktop : mobile;
          const destination = isMobile ? mobile : desktop;
          if (source.childNodes.length) moveContent(source, destination);
        });
        if (!this.productMediaWrapper) return;
        const blocks = this.querySelectorAll(`.product__block[data-show-below-media="true"]`);

        blocks.forEach((block) => {
          const blockId = block.dataset.blockId;
          const blockPlacement = this.productMediaWrapper.querySelector(`[data-block-id="${blockId}"]`);
          if (!blockPlacement) return;

          if (isMobile) {
            const isMoveToMedia = block.hasAttribute('data-moved-below-media');
            if (isMoveToMedia) {
              moveContent(blockPlacement, block);
              block.removeAttribute('data-moved-below-media');
            }
          } else if (!block.hasAttribute('data-moved-below-media')) {
            moveContent(block, blockPlacement);
            block.setAttribute('data-moved-below-media', '');
          }
        });
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber?.();
        this.onVariantChangeUnsubscriber = undefined;
        this.cartUpdateUnsubscriber?.();
        this.cartUpdateUnsubscriber = undefined;
        this.abortController?.abort();
        this.quantityAbortController?.abort();
        this.abortController = undefined;
        this.quantityAbortController = undefined;
        this.formatInfoAbortController?.abort();
        this.formatInfoAbortController = undefined;
        this.titleFitObserver?.disconnect();
        this.titleFitObserver = undefined;
        if ('cancelIdleCallback' in window && this.titleFitIdle !== undefined) {
          cancelIdleCallback(this.titleFitIdle);
        } else if (this.titleFitIdle !== undefined) {
          clearTimeout(this.titleFitIdle);
        }
        this.titleFitIdle = undefined;
        cancelAnimationFrame(this.titleFitFrame);
        this.titleFitFrame = undefined;
        this.requestSequence += 1;
        this.quantityRequestSequence += 1;
        clearTimeout(this.mediaReadyTimer);
        this.mediaReadyTimer = undefined;
        document.removeEventListener('matchMobile', this.blockPositionHandler);
        document.removeEventListener('unmatchMobile', this.blockPositionHandler);
      }

      initializeProductSwapUtility() {
        this.postProcessHtmlCallbacks.push(() => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues, variantId } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        const requestUrl = variantId && !shouldSwapProduct
          ? this.buildVariantRenderUrl(productUrl, variantId)
          : this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage);

        this.renderProductInfo({
          requestUrl,
          targetId: target.closest('.custom-dropdown')?.querySelector('.custom-dropdown__selected')?.id || target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      get productForm() {
        return this.querySelector('form[is="product-form"]');
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.resetFormState();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const nextProductInfo = html.querySelector(selector);
          const variant = this.getSelectedVariant(nextProductInfo);

          this.updateURL(productUrl, variant?.id, nextProductInfo);

          if (updateFullPage) {
            const currentTitle = document.querySelector('head title');
            const nextTitle = html.querySelector('head title');
            if (currentTitle && nextTitle) currentTitle.textContent = nextTitle.textContent;
            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks,
            );
            HTMLUpdateUtility.viewTransition(
              document.getElementById('shopify-section-sticky-atc-bar'),
              html.getElementById('shopify-section-sticky-atc-bar'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks,
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks,
            );
          }

          this.currentVariant = variant;
        };
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateProductResourceSelectors(html);
          this.updateURL(productUrl, variant?.id);
          this.updateShareUrl(variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(variant);

          const updateSourceFromDestination = (id, shouldHide = () => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Barcode', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Badges', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('PricePerItem', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Volume');

          const seriesSource = html.getElementById('Series-' + this.sectionId);
          const seriesDestination = this.querySelector('#Series-' + this.dataset.section);
          if (seriesSource && seriesDestination) {
            seriesDestination.className = seriesSource.className;
            seriesDestination.dataset.seriesCurrentVariant =
              seriesSource.dataset.seriesCurrentVariant || '';
            seriesDestination.innerHTML = seriesSource.innerHTML;
          }

          this.updateQuantityRules(this.sectionId, this.productId, html);
          this.querySelector(`#QuantityRules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#VolumeNote-${this.dataset.section}`)?.classList.remove('hidden');

          HTMLUpdateUtility.viewTransition(
            document.querySelector(`#SizeChart-${this.sectionId}`),
            html.querySelector(`#SizeChart-${this.sectionId}`),
            this.preProcessHtmlCallbacks,
            this.postProcessHtmlCallbacks,
          );

          const addButtonUpdated = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
          this.toggleAddButton(
            addButtonUpdated ? addButtonUpdated.hasAttribute('disabled') : true,
            FoxTheme.variantStrings.soldOut,
          );

          this.currentVariant = variant;
          document.dispatchEvent(
            new CustomEvent('variant:changed', {
              detail: {
                variant: this.currentVariant,
              },
            }),
          );

          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        if (!shouldFetchFullPage) params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      getSelectedVariant(productInfoNode) {
        if (!productInfoNode) return null;
        const selectedVariant = productInfoNode.querySelector('ml-product-options [data-selected-variant]')?.innerHTML;
        if (!selectedVariant) return null;
        try {
          return JSON.parse(selectedVariant);
        } catch (error) {
          console.error('Unable to parse the selected product variant.', error);
          return null;
        }
      }

      normalizeReadableType(value) {
        const normalized = String(value || '')
          .trim()
          .toLowerCase()
          .replace(/[\s_]+/g, '-');
        return ['field-manual', 'hardcover', 'paperback', 'reflowable', 'fixed-format'].includes(normalized)
          ? normalized
          : null;
      }

      getReadableTypeData(sourceNode = this) {
        const dataElement = sourceNode?.querySelector?.('ml-product-options [data-readable-type-variants]');
        if (!dataElement) return null;

        try {
          const entries = JSON.parse(dataElement.textContent);
          if (!Array.isArray(entries)) return null;

          const byType = new Map();
          const byVariant = new Map();

          entries.forEach((entry) => {
            const type = this.normalizeReadableType(entry?.type);
            const variantId = String(entry?.variantId || '');
            const sourceVariantId = String(entry?.sourceVariantId || '');
            if (!type || !/^\d+$/.test(variantId) || !/^\d+$/.test(sourceVariantId)) return;

            if (!byType.has(type)) byType.set(type, variantId);
            byVariant.set(sourceVariantId, type);
            byVariant.set(variantId, type);
          });

          return byType.size ? { byType, byVariant } : null;
        } catch (error) {
          console.error('Unable to parse readable product URL data.', error);
          return null;
        }
      }

      getReadableTypeForVariant(variantId, sourceNode = this) {
        if (!variantId) return null;
        return this.getReadableTypeData(sourceNode)?.byVariant.get(String(variantId)) || null;
      }

      buildReadableProductUrl(url, variantId, sourceNode = this, absolute = false) {
        const sourceUrl = new URL(url || this.dataset.url || window.location.pathname, window.location.origin);
        const currentUrl = new URL(window.location.href);
        const resultUrl = new URL(sourceUrl.pathname, sourceUrl.origin);
        const internalParameters = new Set(['variant', 'type', 'section_id', 'option_values']);

        currentUrl.searchParams.forEach((value, key) => {
          if (!internalParameters.has(key)) resultUrl.searchParams.append(key, value);
        });
        sourceUrl.searchParams.forEach((value, key) => {
          if (!internalParameters.has(key) && !resultUrl.searchParams.has(key)) {
            resultUrl.searchParams.append(key, value);
          }
        });

        const readableType = this.getReadableTypeForVariant(variantId, sourceNode);
        if (readableType) {
          resultUrl.searchParams.set('type', readableType);
        }
        if (variantId) {
          // Keep Shopify's native variant parameter alongside the readable
          // type token so analytics and direct deep links resolve the same SKU.
          resultUrl.searchParams.set('variant', variantId);
        }

        resultUrl.hash = sourceUrl.hash || currentUrl.hash;
        return absolute ? resultUrl.href : `${resultUrl.pathname}${resultUrl.search}${resultUrl.hash}`;
      }

      buildVariantRenderUrl(url, variantId) {
        const requestUrl = new URL(url || this.dataset.url, window.location.origin);
        requestUrl.searchParams.delete('type');
        requestUrl.searchParams.delete('option_values');
        requestUrl.searchParams.set('variant', variantId);
        requestUrl.searchParams.set('section_id', this.sectionId);
        return `${requestUrl.pathname}${requestUrl.search}`;
      }

      hydrateReadableTypeUrl() {
        const readableTypeData = this.getReadableTypeData();
        if (!this.currentVariant) return;

        const currentUrl = new URL(window.location.href);
        if (!readableTypeData) {
          if (currentUrl.searchParams.has('variant') || currentUrl.searchParams.has('type')) {
            this.updateURL(this.dataset.url, this.currentVariant.id);
          }
          return;
        }

        const requestedType = this.normalizeReadableType(currentUrl.searchParams.get('type'));
        const selectedType = readableTypeData.byVariant.get(String(this.currentVariant.id));
        const fallbackType = selectedType && readableTypeData.byType.has(selectedType) ? selectedType : null;
        const resolvedType = requestedType && readableTypeData.byType.has(requestedType)
          ? requestedType
          : fallbackType;
        const canonicalVariantId = resolvedType ? readableTypeData.byType.get(resolvedType) : null;

        if (canonicalVariantId && String(this.currentVariant.id) !== canonicalVariantId) {
          this.renderProductInfo({
            requestUrl: this.buildVariantRenderUrl(this.dataset.url, canonicalVariantId),
            targetId: '',
            callback: this.handleUpdateProductInfo(this.dataset.url),
          });
          return;
        }

        this.updateURL(this.dataset.url, this.currentVariant.id);
        this.updateShareUrl(this.currentVariant.id);
      }

      async renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();
        const controller = this.abortController;
        const requestSequence = ++this.requestSequence;
        const retry = () => this.renderProductInfo({requestUrl, targetId, callback});
        this.variantSelectors?.setSelectionPhase('loading', retry);

        try {
          const response = await fetch(requestUrl, { signal: controller.signal });
          if (!response.ok) throw new Error(`Unable to update product information (${response.status})`);
          const responseText = await response.text();
          if (
            controller.signal.aborted ||
            requestSequence !== this.requestSequence ||
            !this.isConnected
          ) {
            return;
          }

          const html = new DOMParser().parseFromString(responseText, 'text/html');
          // Restore the replaced picker only while it still owns focus. A user
          // who has moved to another control must not be pulled back on response.
          const restorePickerFocus = this.variantSelectors?.contains(document.activeElement);
          callback(html);
          this.variantSelectors?.setSelectionPhase(this.getSelectedVariant(this) ? 'ready' : 'unavailable');
          if (requestSequence === this.requestSequence && restorePickerFocus) {
            document.getElementById(targetId)?.focus({ preventScroll: true });
          }
        } catch (error) {
          if (error.name !== 'AbortError' && requestSequence === this.requestSequence) {
            this.variantSelectors?.setSelectionPhase('error', retry);
            console.error(error);
          }
        } finally {
          if (requestSequence === this.requestSequence) {
            this.pendingRequestUrl = null;
            this.abortController = undefined;
          }
        }
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('ml-product-options');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      updateProductResourceSelectors(html) {
        const sourceSelector = html.querySelector('product-resource-selectors');
        const destinationSelector = this.querySelector('product-resource-selectors');
        if (!sourceSelector || !destinationSelector) return;

        // Language membership and retailer destinations are both specific to
        // the selected format. The section response is the authoritative
        // Liquid render for that variant, so replace the controller as one
        // unit and let its custom-element lifecycle bind the new contents.
        destinationSelector.replaceWith(sourceSelector.cloneNode(true));
      }

      updateURL(url, variantId, sourceNode = this) {
        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', this.buildReadableProductUrl(url, variantId, sourceNode));
      }

      updateVariantInputs(variantId) {
        document
          .querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`)
          .forEach((productForm) => {
            const input = productForm.querySelector('input[name="id"]');
            if (!input) return;
            input.value = variantId ?? '';
            input.dispatchEvent(new Event('change', { bubbles: true }));
          });
      }

      updateMedia(variant) {
        const productMedia = this.querySelector(`[id^="MediaGallery-${this.dataset.section}"]`);
        if (!productMedia) return; // Early return if productMedia is not found
        clearTimeout(this.mediaReadyTimer);
        this.mediaReadyTimer = undefined;
        let lastMediaError;

        const setActiveMedia = () => {
          try {
            if (typeof productMedia.setActiveMedia === 'function') {
              productMedia.init();
              productMedia.setActiveMedia(variant);
              return true; // Indicate success
            }
          } catch (error) {
            lastMediaError = error;
          }
          return false; // Indicate failure
        };

        if (setActiveMedia()) return;

        let attempts = 0;
        const waitForMediaGallery = () => {
          this.mediaReadyTimer = undefined;
          if (!this.isConnected || setActiveMedia()) return;
          if (attempts >= 50) {
            if (lastMediaError) console.error('Unable to initialize product media.', lastMediaError);
            return;
          }
          attempts += 1;
          this.mediaReadyTimer = setTimeout(waitForMediaGallery, 100);
        };
        waitForMediaGallery();
      }

      updateShareUrl(variantId) {
        if (!variantId) return;
        const shareButton = document.getElementById(`ProductShare-${this.dataset.section}`);
        if (!shareButton || !shareButton.updateUrl) return;
        shareButton.updateUrl(this.buildReadableProductUrl(this.dataset.url, variantId, this, true));
      }

      toggleAddButton(disable = true, text) {
        const productForm = document.getElementById(`product-form-${this.dataset.section}`);
        if (!productForm) return;
        const addButton = productForm.querySelector('[name="add"]');
        const addButtonText = productForm.querySelector('[name="add"] > span');
        if (!addButton) return;

        if (disable) {
          addButton.setAttribute('disabled', 'disabled');
          addButton.dataset.uiState =
            text === FoxTheme.variantStrings.soldOut
              ? 'sold-out'
              : text === FoxTheme.variantStrings.unavailable
                ? 'unavailable'
                : 'disabled';
          if (text && addButtonText) addButtonText.textContent = text;
        } else {
          addButton.removeAttribute('disabled');
          delete addButton.dataset.uiState;
          if (addButtonText) addButtonText.textContent = FoxTheme.variantStrings.addToCart;
        }
      }

      setUnavailable() {
        this.toggleAddButton(true, FoxTheme.variantStrings.unavailable);
        const price = document.getElementById(`price-${this.dataset.section}`);
        const inventory = document.getElementById(`Inventory-${this.dataset.section}`);
        const sku = document.getElementById(`Sku-${this.dataset.section}`);

        if (price) price.classList.add('hidden');
        if (inventory) inventory.classList.add('hidden');
        if (sku) sku.classList.add('hidden');
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.setQuantityBoundries();
        if (!this.hasAttribute('data-original-section')) {
          this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
            FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRulesHandler,
          );
        }
      }

      setQuantityBoundries() {
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityBoundries, {
          data: {
            sectionId: this.sectionId,
            productId: this.productId,
          },
        });
      }

      async fetchQuantityRules() {
        const currentVariantId = this.productForm?.productIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart')?.classList.add('btn--loading');
        this.quantityAbortController?.abort();
        this.quantityAbortController = new AbortController();
        const controller = this.quantityAbortController;
        const requestSequence = ++this.quantityRequestSequence;

        try {
          const response = await fetch(
            `${this.getAttribute('data-url')}?variant=${currentVariantId}&section_id=${this.sectionId}`,
            { signal: controller.signal },
          );
          if (!response.ok) throw new Error(`Unable to update quantity rules (${response.status})`);
          const responseText = await response.text();
          if (
            controller.signal.aborted ||
            requestSequence !== this.quantityRequestSequence ||
            !this.isConnected
          ) {
            return;
          }
          const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
          this.updateQuantityRules(this.sectionId, this.productId, parsedHTML);
        } catch (error) {
          if (error.name !== 'AbortError' && requestSequence === this.quantityRequestSequence) {
            console.error(error);
          }
        } finally {
          if (requestSequence === this.quantityRequestSequence) {
            this.querySelector('.quantity__rules-cart')?.classList.remove('btn--loading');
            this.quantityAbortController = undefined;
          }
        }
      }

      updateQuantityRules(sectionId, productId, parsedHTML) {
        if (!this.quantityInput) return;

        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityRules, {
          data: {
            sectionId,
            productId,
            parsedHTML,
          },
        });

        this.setQuantityBoundries();
      }
    },
  );
}

if (!customElements.get('product-promotion-alert')) {
  customElements.define(
    'product-promotion-alert',
    class ProductPromotionAlert extends HTMLElement {
      constructor() {
        super();

        this.buttonEl = this.querySelector('button');
        this.onClickHandler = this.onClick.bind(this);
      }

      connectedCallback() {
        this.abortController?.abort();
        this.abortController = new AbortController();
        this.buttonEl?.addEventListener('click', this.onClickHandler, {
          signal: this.abortController.signal,
        });
      }

      onClick(evt) {
        evt.preventDefault();

        this.closest('.product__block')?.classList.add('hidden');
      }

      disconnectedCallback() {
        this.abortController?.abort();
      }
    },
  );
}
